import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchModules } from "../../../Store/moduleSlice";
import { useNavigate } from "react-router-dom";
import {
  FiDatabase,
  FiTrendingUp,
  FiShield,
  FiBarChart2,
  FiChevronRight,
  FiLayers,
  FiLock,
  FiActivity,
} from "react-icons/fi";
import { Button } from "antd";

const AuditUniverse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { modules, loading, error } = useSelector((state) => state.module);

  useEffect(() => {
    dispatch(fetchModules());
  }, [dispatch]);

  const formatName = (name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get icon based on module name or index
  const getModuleIcon = (name, index) => {
    const icons = [
      <FiDatabase className="w-6 h-6" />,
      <FiTrendingUp className="w-6 h-6" />,
      <FiShield className="w-6 h-6" />,
      <FiBarChart2 className="w-6 h-6" />,
      <FiLayers className="w-6 h-6" />,
      <FiLock className="w-6 h-6" />,
      <FiActivity className="w-6 h-6" />,
    ];
    return icons[index % icons.length];
  };

  // Get gradient based on module index
  const getCardGradient = (index) => {
    const gradients = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-emerald-500 to-emerald-600",
      "from-orange-500 to-orange-600",
      "from-red-500 to-red-600",
      "from-indigo-500 to-indigo-600",
      "from-teal-500 to-teal-600",
    ];
    return gradients[index % gradients.length];
  };

  const handleModuleClick = (module) => {
    navigate("/data-injection/SubModulePage", {
      state: {
        moduleName: module.name,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="relative bg-gradient-to-r from-[#36096d] via-[#4a0e8f] to-[#37d5d6] text-white rounded-2xl shadow-1xl border-b-4 border-[#37d5d6] overflow-hidden">
        {/* Animated gradient background with your project colors */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#36096d]/20 via-[#37d5d6]/10 to-[#36096d]/20 animate-pulse"></div>

        {/* Glass morphism effect */}
        <div className="absolute inset-0 backdrop-blur-[2px]"></div>

        <div className="relative p-3 z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl lg:text-2xl font-bold text-white bg-clip-text text-transparent">
                  Audit-Universe
                </h1>
              </div>
            </div>

            <button
            onClick={() => navigate(-1)}
             className="group flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#36096d] to-[#37d5d6] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20">
              <svg
                className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Bottom shine effect with your theme colors */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#37d5d6] to-transparent"></div>
      </div>
      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-3">
        {Array.isArray(modules) &&
          modules.map((item, idx) => (
            <div
              key={item.technicalName}
              onClick={() => handleModuleClick(item)}
              className="group relative overflow-hidden rounded-[22px] bg-[#eef1f5] 
             shadow-sm hover:shadow-2xl transition-all duration-300 
             cursor-pointer hover:-translate-y-1 border border-gray-200"
            >
              {/* Right curved accent */}
              <div
                className={`absolute top-0 right-0 h-full w-20 
    bg-gradient-to-b ${getCardGradient(idx)} 
    rounded-l-full opacity-80 group-hover:w-24 transition-all duration-300`}
              ></div>

              {/* Soft glow effect */}
              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition duration-300"></div>

              {/* Content */}
              <div className="relative z-10 p-6 h-[150px] flex flex-col justify-between">
                {/* Top */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
                      {formatName(item.name)}
                    </h3>

                    <p className="text-sm text-gray-500 mt-2 max-w-[75%]">
                      Audit & process management module
                    </p>
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center 
        bg-white/70 backdrop-blur-md shadow-md text-gray-700`}
                  >
                    {getModuleIcon(item.name, idx)}
                  </div>
                </div>

                {/* Bottom */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    Open Module
                  </span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center
        bg-gradient-to-r ${getCardGradient(idx)}
        text-white shadow-md group-hover:scale-110 transition`}
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default AuditUniverse;
